"use client"

import * as React from "react"
import { Search } from "lucide-react"

import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useCoursesInfinite } from "@/features/courses/hooks"
import type { CourseOut } from "@/features/courses/types"
import {
  COURSE_FILTER_ALL,
  DATE_PRESET_OPTIONS,
  PAYMENT_MODE_FILTER_OPTIONS,
  type DatePreset,
  type InvoiceListFilterState,
  type PaymentModeFilterValue,
} from "@/features/invoices/filters"

import { ActionRequiredFilter } from "./ActionRequiredFilter"
import { StatusFilter } from "./StatusFilter"

export interface InvoiceFiltersProps {
  value: InvoiceListFilterState
  onChange: (patch: Partial<InvoiceListFilterState>) => void
}

export function InvoiceFilters({ value, onChange }: InvoiceFiltersProps) {
  const courses = useCoursesInfinite()
  const allCourses = React.useMemo<CourseOut[]>(
    () => courses.data?.pages.flatMap((p) => p.items) ?? [],
    [courses.data],
  )

  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const fetchNextPageRef = React.useRef(courses.fetchNextPage)
  React.useEffect(() => {
    fetchNextPageRef.current = courses.fetchNextPage
  })
  const { hasNextPage, isFetchingNextPage } = courses
  React.useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    if (!hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !isFetchingNextPage) {
            fetchNextPageRef.current()
          }
        }
      },
      { root: null, rootMargin: "0px", threshold: 0 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage])

  return (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search name, email, course, memo, invoice number…"
          className="pl-9"
          value={value.searchText}
          onChange={(e) => onChange({ searchText: e.target.value })}
          aria-label="Search invoices"
        />
      </div>
      <StatusFilter
        value={value.status}
        onChange={(status) => onChange({ status })}
      />
      <Select
        value={value.paymentMode}
        onValueChange={(next) =>
          onChange({ paymentMode: next as PaymentModeFilterValue })
        }
      >
        <SelectTrigger className="w-[150px]" aria-label="Payment mode filter">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PAYMENT_MODE_FILTER_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={value.datePreset}
        onValueChange={(next) =>
          onChange({ datePreset: next as DatePreset })
        }
      >
        <SelectTrigger className="w-[140px]" aria-label="Date range">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DATE_PRESET_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={value.courseId}
        onValueChange={(next) => onChange({ courseId: next })}
      >
        <SelectTrigger className="w-[180px]" aria-label="Course filter">
          <SelectValue placeholder="Course" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={COURSE_FILTER_ALL}>All Courses</SelectItem>
          {allCourses.map((course) => (
            <SelectItem key={course.id} value={String(course.id)}>
              {course.title}
            </SelectItem>
          ))}
          <div ref={sentinelRef} data-testid="course-filter-sentinel" />
          {courses.isFetchingNextPage && (
            <div className="py-2 text-center text-caption text-muted-foreground">
              Loading more…
            </div>
          )}
        </SelectContent>
      </Select>
      <ActionRequiredFilter
        value={value.actionRequired}
        onChange={(next) => onChange({ actionRequired: next })}
      />
    </div>
  )
}
